import { useMsal } from '@azure/msal-react';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useHeap } from '@configs/heap';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token/useGetToken';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  ReservationsDashboardAlertsPanelsProps,
  ReservationsDashboardAlertsPanelsWrapperProps,
} from './ReservationsDashboardAlertsPanels.types';

const ReservationsDashboardAlertsPanels = React.memo(
  ({
    isOpen,
    activeAlertType,
    onClose,
  }: Readonly<ReservationsDashboardAlertsPanelsWrapperProps>): React.ReactElement | null => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const { selectedOrganization } = useOrganizations();
    const getToken = useGetToken();
    const navigate = useNavigate();
    const heapIdentity = useHeap();

    const [RemoteComponent, setRemoteComponent] =
      React.useState<React.ComponentType<ReservationsDashboardAlertsPanelsProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    const loadRouter = React.useCallback(async (): Promise<void> => {
      try {
        const { default: Router } =
          await import('reservations_mfe/ReservationsDashboardAlertsPanels');
        setRemoteComponent(() => Router);
      } catch (error) {
        setLoadError(error as Error);
      }
    }, []);

    React.useEffect(() => {
      void loadRouter();
    }, [loadRouter]);

    const handleNavigateToEtca = React.useCallback(
      (reservationRequestId: string): void => {
        navigate(
          `/manage/${selectedOrganization?.customerId}/etca/${reservationRequestId}`
        );
      },
      [navigate, selectedOrganization?.customerId]
    );

    const renderFallbackPanel = React.useCallback(
      (content: React.ReactNode): React.ReactElement => {
        return (
          <CobaltSidePanel
            size={isMobileOrTablet ? 'large' : 'small'}
            isOpen={isOpen}
            mode={isMobileOrTablet ? 'over' : 'push'}
            showCloseButton
            onClose={onClose}
            title="Your alerts"
            data-testid="reservations-dashboard-alerts-panel-fallback"
          >
            {content}
          </CobaltSidePanel>
        );
      },
      [isMobileOrTablet, isOpen, onClose]
    );

    if (loadError) {
      return renderFallbackPanel(
        <InlineFeedback type="internal-server-error" showGoHome={false} />
      );
    }

    if (!RemoteComponent || !selectedOrganization || !heapIdentity) {
      return renderFallbackPanel(<ContentLoader />);
    }

    return (
      <React.Suspense fallback={renderFallbackPanel(<ContentLoader />)}>
        <RemoteComponent
          msalInstance={instance}
          user={authData}
          customerId={selectedOrganization.customerId}
          organizationId={selectedOrganization.id}
          permissions={permissions}
          heapIdentity={heapIdentity}
          getToken={getToken}
          isOpen={isOpen}
          activeAlertType={activeAlertType}
          onClose={onClose}
          onNavigateToEtca={handleNavigateToEtca}
        />
      </React.Suspense>
    );
  }
);

ReservationsDashboardAlertsPanels.displayName =
  'ReservationsDashboardAlertsPanels';

export { ReservationsDashboardAlertsPanels };
